import React, { FC, useEffect, useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, Fn, Text, Button, ButtonVariant, TextVariant, Modal } from '@mprolab/uikit'
import { observer } from 'mobx-react-lite'
import { ProgressState, indexNodeStore } from 'mobxStores/IndexNodeStore'
import { LicenseStatus } from '@mprolab/sdk'
import useGetSingleAccessToken from 'hooks/useGetSingleAccessToken'
import { useUpdateLicenseStatusMutation } from 'state/services/indexNode/indexNode'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { TransactionView } from 'components/TransactionView'
import { TransactionViewType } from 'components/TransactionView/TransactionView'
import { useTranslation } from 'react-i18next'
import { logMessage } from 'utils/logMessage'
import { getErrorCodesDescription } from 'utils/getErrorCodesDescription'

const MainWrapper = styled(Flex)`
  width: 557px;
  flex-direction: column;

  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
  }
`

interface TerminateModalProps {
  onDismiss?: Fn
}

const TerminateNodeModal: FC<TerminateModalProps> = observer(({ onDismiss }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { account } = useWeb3React()
  const { progress, node, error } = indexNodeStore

  const { obtainAccessToken } = useGetSingleAccessToken()
  const [updateLicenseStatus] = useUpdateLicenseStatusMutation()

  const terminateLicenseHandler = async () => {
    indexNodeStore.setProgress(ProgressState.PENDING)
    await obtainAccessToken(async (accessToken) => {
      try {
        await updateLicenseStatus({
          account,
          accessToken,
          licenseId: node.licenseId,
          status: LicenseStatus.TERMINATED
        }).unwrap()
        indexNodeStore.setProgress(ProgressState.SUCCESS)
      } catch (err) {
        logMessage(err)
        indexNodeStore.setError(err)
      }
    })
  }

  const content = useMemo((): JSX.Element => {
    switch (progress) {
      case ProgressState.INITIAL:
        return (
          <Flex flexDirection="column" fullWidth gap="24px">
            <Text color={colors.gray3}>{t('terminateNode.continue')}</Text>
            <Flex maxWidth="500px">
              <Text color={colors.gray6} variant={TextVariant.BODY_SMALL}>
                {t('terminateNode.description')}
              </Text>
            </Flex>
            <Flex fullWidth alignItems="center" justifyContent="center" gap="16px">
              <Button fullWidth variant={ButtonVariant.OUTLINED} onClick={onDismiss}>
                {t('terminateNode.cancelButton')}
              </Button>
              <Button fullWidth onClick={terminateLicenseHandler}>
                {t('terminateNode.continueButton')}
              </Button>
            </Flex>
          </Flex>
        )
      case ProgressState.CONFIRMING:
      case ProgressState.PENDING:
        return (
          <Flex mt="24px">
            <TransactionView transactionView={TransactionViewType.SIGNING} />
          </Flex>
        )
      case ProgressState.ERROR:
        return (
          <Flex mt="24px">
            <TransactionView
              transactionView={TransactionViewType.FAILED}
              customMsg={getErrorCodesDescription(t, error)}
            />
          </Flex>
        )
      default:
        return <></>
    }
  }, [progress, error])

  useEffect(() => {
    if (progress === ProgressState.SUCCESS) {
      onDismiss()
      indexNodeStore.resetIndexNodeStore()
    }
  }, [progress])

  const licenseName = node?.name ? node?.name : node.licenseId
  const formattedLicenseName = licenseName.length > 20 ? `${licenseName.slice(0, 20)}...` : licenseName

  const title = useMemo((): string => {
    switch (progress) {
      case ProgressState.INITIAL:
        return `${formattedLicenseName} ${t('terminateNode.terminated')}`
      case ProgressState.CONFIRMING:
      case ProgressState.PENDING:
        return t('modal.transactionConfirmation')
      case ProgressState.ERROR:
        return t('modal.somethingWentWrong')
      default:
        return ''
    }
  }, [progress])

  return (
    <Modal {...{ title, onDismiss }}>
      <MainWrapper>{content}</MainWrapper>
    </Modal>
  )
})

export default TerminateNodeModal
